/**
 * What is still missing on the Personal details form, for the appointment page's "complete the patient" arrival.
 *
 * One list, in the order the fields sit on the page:
 *   - every field the form's own schema requires (`personalDetailsSchema`: contact email and number, first and last
 *     name, date of birth, gender),
 *   - the fields the form marks with a star but the schema leaves open (nationality; for a minor the guardian's
 *     relationship, first and last name),
 *   - the identification chain (residency type, ID type, ID number): an adult needs all three, a minor only keeps the
 *     chain consistent (an ID number needs its ID type, an ID type its residency type),
 *   - the Arrived guard's own items (`arrivedGuardStatus`): an adult needs an ID number; a minor needs the patient's or
 *     the guardian's ID number and a guardian relationship.
 */

import {
  personalDetailsSchema,
  type PersonalDetailsFormValues,
} from '@/patients/components/personal-details/personal-details-schema';

export type MissingKey =
  | 'email'
  | 'phoneNumber'
  | 'firstName'
  | 'lastName'
  | 'birthDate'
  | 'gender'
  | 'nationality'
  | 'residencyType'
  | 'idType'
  | 'idNumber'
  | 'guardianRelation'
  | 'guardianFirstName'
  | 'guardianLastName'
  | 'guardianIdNumber';

export interface MissingItem {
  key: MissingKey;
  label: string;
}

/** Page order (contact first, as the form draws it) with the words the form's labels use. */
const ORDER: MissingItem[] = [
  { key: 'email', label: 'Email address' },
  { key: 'phoneNumber', label: 'Contact number' },
  { key: 'firstName', label: 'First name' },
  { key: 'lastName', label: 'Last name' },
  { key: 'birthDate', label: 'Date of birth' },
  { key: 'gender', label: 'Gender' },
  { key: 'nationality', label: 'Nationality' },
  { key: 'residencyType', label: 'Residency type' },
  { key: 'idType', label: 'ID type' },
  { key: 'idNumber', label: 'ID number' },
  { key: 'guardianRelation', label: 'Relationship to patient' },
  { key: 'guardianFirstName', label: 'Guardian first name' },
  { key: 'guardianLastName', label: 'Guardian last name' },
  { key: 'guardianIdNumber', label: 'Guardian ID number' },
];

const SCHEMA_KEYS = new Set<string>(['email', 'phoneNumber', 'firstName', 'lastName', 'birthDate', 'gender']);

const blank = (v: string | null | undefined): boolean => !v || v.trim().length === 0;

export function missingPersonalDetails(values: Partial<PersonalDetailsFormValues>): MissingItem[] {
  const missing = new Set<MissingKey>();

  const parsed = personalDetailsSchema.safeParse(values);
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      const head = String(issue.path[0] ?? '');
      if (SCHEMA_KEYS.has(head)) missing.add(head as MissingKey);
    }
  }

  if (blank(values.nationality?.code) && blank(values.nationality?.display)) missing.add('nationality');

  const guardian = values.guardianIdenitification;
  const ownId = values.identification?.idNumber;
  if (values.isAdult === false) {
    if (blank(guardian?.relationShip?.id) && blank(guardian?.relationId)) missing.add('guardianRelation');
    if (blank(guardian?.firstName)) missing.add('guardianFirstName');
    if (blank(guardian?.lastName)) missing.add('guardianLastName');
    if (blank(ownId) && blank(guardian?.idNumber)) missing.add('guardianIdNumber');
  } else {
    // An adult needs the whole identification chain: residency type, ID type, ID number.
    if (blank(values.residencyType?.code)) missing.add('residencyType');
    if (blank(values.identification?.idType)) missing.add('idType');
    if (blank(ownId)) missing.add('idNumber');
  }
  // Any age: a later choice without the earlier one (the form's own rule, in the same order).
  if (!blank(ownId) && blank(values.identification?.idType)) missing.add('idType');
  if (!blank(values.identification?.idType) && blank(values.residencyType?.code)) missing.add('residencyType');

  return ORDER.filter((item) => missing.has(item.key));
}
